import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Badge from '../UI/Badge';

export default function CourseList({ userRole }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourses();
  }, [userRole]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const endpoint = userRole === 'TEACHER'
        ? '/courses/my/teaching'
        : '/courses/my/enrolled';

      const response = await api.get(endpoint);
      setCourses(response.data.courses || []);
    } catch (err) {
      setError(err.response?.data?.error || '수업 목록을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="text-center py-8">
        <p className="text-danger-500 mb-4">{error}</p>
        <Button onClick={fetchCourses}>다시 시도</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-neutral-800">
          {userRole === 'TEACHER' ? '내 수업' : '수강 중인 수업'}
        </h2>
        {userRole === 'TEACHER' && (
          <Button onClick={() => navigate('/courses/create')}>
            새 수업 만들기
          </Button>
        )}
      </div>

      {courses.length === 0 ? (
        <Card className="text-center py-12">
          <p className="text-neutral-500 mb-4">
            {userRole === 'TEACHER'
              ? '아직 개설한 수업이 없습니다.'
              : '수강 중인 수업이 없습니다.'}
          </p>
          {userRole === 'TEACHER' && (
            <Button onClick={() => navigate('/courses/create')}>
              수업 개설하기
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <Card
              key={course.id}
              hover
              className="cursor-pointer animate-fade-in"
              onClick={() => navigate(`/courses/${course.id}`)}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-bold text-neutral-800 flex-1">
                  {course.title}
                </h3>
                {course.isActive ? (
                  <Badge variant="success">진행중</Badge>
                ) : (
                  <Badge variant="default">종료</Badge>
                )}
              </div>

              {course.description && (
                <p className="text-sm text-neutral-600 mb-4 line-clamp-2">
                  {course.description}
                </p>
              )}

              <div className="flex items-center justify-between text-sm text-neutral-500">
                <span>수업 코드: {course.courseCode}</span>
                {userRole === 'TEACHER' && course._count && (
                  <span>{course._count.enrollments || 0}명 수강</span>
                )}
              </div>

              {userRole === 'TEACHER' && course.teacher && (
                <div className="mt-3 pt-3 border-t border-neutral-100">
                  <p className="text-sm text-neutral-600">
                    담당 교사: {course.teacher.username}
                  </p>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
